<template>
    <div>
        <AntdvInput
            v-if="!data.open"
            :value="inputValueComputed"
            v-maska="maskComputed"
            :placeholder="placeholderComputed"
            :disabled="disabled"
            :size="size"
            @update:value="onUpdateInputValue"
            @blur="onBlurInputValue"
        >
            <template v-slot:suffix>
                <CIcon file="close.svg" v-if="value && !disabled" class="mr-2" @click="() => onUpdateValue(null)"></CIcon>
                <CIcon file="calendar.svg" v-if="!disabled" @click="() => (data.open = true)"></CIcon>
            </template>
        </AntdvInput>
        <RangePicker
            v-else
            :class="`w-100 custom-format format-${format}`"
            :value="value"
            @update:value="onUpdateValue"
            :format="format"
            :valueFormat="valueFormat"
            :inputReadOnly="true"
            :showTime="showTime"
            :open="data.open"
            :mode="data.mode"
            :placeholder="label"
            :disabled="disabled"
            :dropdownClassName="`custom-format format-${format}`"
            :size="size"
            @change="onChange"
            @openChange="onOpenChange"
            @panelChange="onPanelChange"
        ></RangePicker>
    </div>
</template>

<script>
import { computed, defineAsyncComponent, defineComponent, reactive, watchEffect } from "vue";
import { Input, DatePicker } from "ant-design-vue";
import moment from "moment";
import {
    formatInputValue,
    formatToMask,
    formatToPlaceholder,
    onUpdateInputValue,
    onBlurInputValue,
    onUpdateValue,
    createOnChange,
    createOnOpenChange,
    createOnPanelChange,
} from "./range-picker.logic";

export default defineComponent({
    components: {
        AntdvInput: Input,
        RangePicker: DatePicker.RangePicker,
        CIcon: defineAsyncComponent(() => import("../../../icon/CIcon")),
    },
    props: {
        value: {},
        format: {
            default: (props) =>
                !props.showTime
                    ? "DD/MM/YYYY"
                    : props.showTime === true || props.showTime.format?.includes("ss")
                    ? "DD/MM/YYYY HH:mm:ss"
                    : "DD/MM/YYYY HH:mm",
        },
        valueFormat: {
            default: (props) =>
                !props.showTime
                    ? "YYYY-MM-DD"
                    : props.showTime === true || props.showTime.format?.includes("ss")
                    ? "YYYY-MM-DD[T]HH:mm:ssZ"
                    : "YYYY-MM-DD[T]HH:mm:00Z",
        },
        valueFormat2: {
            default: (props) =>
                !props.showTime
                    ? "YYYY-MM-DD"
                    : props.showTime === true || props.showTime.format?.includes("ss")
                    ? "YYYY-MM-DD[T]HH:mm:ss.999Z"
                    : "YYYY-MM-DD[T]HH:mm:59.999Z",
        },
        label: {},
        disabled: {},
        showTime: { default: false },
        size: { default: "default" },
    },
    emits: ["update:value"],
    setup(props, { emit }) {
        const data = reactive({
            inputValue: "",
            open: false,
            mode: "date",
        });
        watchEffect(() => {
            let inputValue = "";
            const value0 = moment(props.value?.[0], props.valueFormat, true);
            const value1 = moment(moment(props.value?.[1], [props.valueFormat, props.valueFormat2], true).format(props.valueFormat2));
            if (value0.isValid()) {
                inputValue += value0.format(props.format);
            }
            if (value0.isValid() && value1.isValid()) {
                inputValue += " - " + value1.format(props.format);
            }
            data.inputValue = inputValue;
        });
        const inputValueComputed = computed(() => {
            let inputValue = "";
            let inputValueStr = "";
            const value = data.inputValue.split(" - ");
            const value0Moment = moment(value[0], props.format, true);
            const value1Moment = moment(moment(value[1], props.format, true).format(props.valueFormat2));
            const value0 = formatInputValue(value[0], props.format);
            const value1 = formatInputValue(value[1], props.format);
            if (value0) {
                if (value0Moment.isSame(props.value?.[0])) {
                    inputValue += value0;
                    inputValueStr += value0;
                } else {
                    inputValue += value[0];
                    inputValueStr += moment(props.value?.[0], props.valueFormat, true).format(props.format);
                }
            }
            if (value0 && value1) {
                if (moment(value1Moment.format(props.valueFormat2)).isSame(props.value?.[1])) {
                    inputValue += " - " + value1;
                    inputValueStr += " - " + value1;
                } else {
                    inputValue += " - " + value[1];
                    inputValueStr += " - " + moment(props.value?.[1], props.valueFormat2, true).format(props.format);
                }
            }
            // eslint-disable-next-line vue/no-side-effects-in-computed-properties
            data.inputValue = inputValueStr;
            return inputValue;
        });
        const maskComputed = computed(() => formatToMask(props.format) + " - " + formatToMask(props.format));
        const placeholderComputed = computed(() => formatToPlaceholder(props.format) + " - " + formatToPlaceholder(props.format));
        return {
            data,
            inputValueComputed,
            maskComputed,
            placeholderComputed,
            onUpdateInputValue: onUpdateInputValue({ props, data, emit }),
            onBlurInputValue: onBlurInputValue({ props, data, emit }),
            onUpdateValue: onUpdateValue({ props, data, emit }),
            onChange: createOnChange({ props, data, emit }),
            onOpenChange: createOnOpenChange({ props, data, emit }),
            onPanelChange: createOnPanelChange({ props, data, emit }),
        };
    },
});
</script>
